import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Camera, UploadSimple, SpinnerGap, ArrowRight } from "@phosphor-icons/react";
import { api } from "../lib/api";

export default function ScanModal({ open, onClose }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState("");
  const [mime, setMime] = useState("image/jpeg");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  if (!open) return null;

  const reset = () => {
    setPreview("");
    setResult(null);
    setError("");
    setLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = async (file) => {
    if (!file) return;
    setError("");
    setResult(null);
    setMime(file.type || "image/jpeg");
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result?.toString() || "";
      setPreview(dataUrl);
      const b64 = dataUrl.split(",")[1];
      setLoading(true);
      try {
        const r = await api.scanProduct(b64, file.type || "image/jpeg");
        setResult(r);
      } catch (e) {
        setError(e?.response?.data?.detail?.toString() || "Scan failed");
      } finally {
        setLoading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div
      data-testid="scan-modal"
      className="fixed inset-0 z-50 bg-[#1C1B1A]/50 backdrop-blur-sm flex items-start justify-center pt-[8vh] px-4"
      onMouseDown={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div className="w-full max-w-2xl bg-white border border-[#1C1B1A] max-h-[86vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E2DC]">
          <div>
            <div className="eyebrow">AI product identifier</div>
            <div className="font-heading text-xl mt-1">Scan or upload a product</div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            data-testid="scan-close"
            className="w-8 h-8 flex items-center justify-center text-[#6E6B65] hover:text-[#1C1B1A]"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto thin-scroll">
          {!preview && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E5E2DC] border border-[#E5E2DC]">
              <button
                type="button"
                data-testid="scan-upload-btn"
                onClick={() => fileRef.current?.click()}
                className="bg-white p-6 flex flex-col items-center gap-2 hover:bg-[#EFECE6] transition-colors"
              >
                <UploadSimple size={28} className="text-[#A66E4E]" />
                <div className="font-heading text-sm">Upload photo</div>
                <div className="text-[11px] text-[#6E6B65] text-center">JPEG / PNG / WEBP</div>
              </button>
              <label
                data-testid="scan-camera-btn"
                className="bg-white p-6 flex flex-col items-center gap-2 hover:bg-[#EFECE6] transition-colors cursor-pointer"
              >
                <Camera size={28} className="text-[#A66E4E]" />
                <div className="font-heading text-sm">Click from camera</div>
                <div className="text-[11px] text-[#6E6B65] text-center">Opens rear camera on mobile</div>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
              </label>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </div>
          )}

          {preview && (
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
              <div className="sm:col-span-2 bg-[#EFECE6] border border-[#E5E2DC] aspect-square overflow-hidden">
                <img src={preview} alt="scan preview" className="w-full h-full object-cover" />
              </div>
              <div className="sm:col-span-3">
                {loading && (
                  <div className="flex items-center gap-3 text-[#6E6B65]" data-testid="scan-loading">
                    <SpinnerGap size={20} className="animate-spin" />
                    <span>Identifying with Claude Sonnet 4.5…</span>
                  </div>
                )}
                {error && (
                  <div className="text-[13px] text-[#D93636] bg-[#FBEAE9] px-3 py-2" data-testid="scan-error">
                    {error}
                  </div>
                )}
                {result && !loading && (
                  <div data-testid="scan-result">
                    <div className="eyebrow">AI identification</div>
                    <div className="font-heading text-xl mt-1">
                      {result.brand_guess || "Unknown brand"}
                    </div>
                    <div className="font-accent italic text-2xl text-[#1C1B1A]">
                      {result.product_guess || "Could not read label"}
                    </div>
                    <div className="mt-2 text-[11px] uppercase tracking-wider text-[#6E6B65]">
                      confidence · <span className="text-[#1C1B1A] font-semibold">{result.confidence}</span>
                    </div>
                    {result.notes && (
                      <p className="text-[12.5px] text-[#6E6B65] mt-3 leading-snug">{result.notes}</p>
                    )}

                    <div className="mt-5 eyebrow">Matches in our database</div>
                    <div className="mt-2 space-y-px bg-[#E5E2DC]">
                      {result.matches?.length ? (
                        result.matches.map((m) => (
                          <Link
                            to={`/product/${m.id}`}
                            onClick={handleClose}
                            key={m.id}
                            data-testid="scan-match-item"
                            className="flex items-center gap-3 bg-white px-3 py-2.5 hover:bg-[#EFECE6] transition-colors"
                          >
                            <div className="w-10 h-10 bg-[#EFECE6] shrink-0 overflow-hidden">
                              {m.image_url && (
                                <img src={m.image_url} alt="" className="w-full h-full object-cover" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="eyebrow text-[9px]">{m.brand}</div>
                              <div className="font-heading text-sm truncate">{m.name}</div>
                            </div>
                            <ArrowRight size={14} className="text-[#A66E4E]" />
                          </Link>
                        ))
                      ) : (
                        <div className="bg-white px-3 py-3 text-sm text-[#6E6B65]">
                          No direct matches. Try searching manually.
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  data-testid="scan-retry"
                  onClick={reset}
                  className="mt-5 text-xs tracking-[0.18em] uppercase font-semibold text-[#A66E4E] hover:text-[#8F5D41]"
                >
                  ← Scan another product
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
